import { z } from 'zod';
import type {
    ListTransactionLogPageResponse,
    TransactionLogListItem,
    TransactionLogOperation,
    TransactionLogSearchRequest,
    TransactionLogTableName,
} from '../transaction-log.types';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import { AuthorizationPaginationLimitQuerySchema } from './authorization.schemas';
import { JsonValueSchema, NonEmptyStringSchema } from './common.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';

export const TransactionLogTableNameSchema: z.ZodType<TransactionLogTableName> = z.enum([
    'users',
    'organizations',
    'merchant_customer_records',
    'role_assignments',
    'single_use_auth_tokens',
    'totp_backup_codes',
    'ipw_customer_links',
    'payment_method_tokens',
    'stored_payment_methods',
    'credit_card_stored_payment_details',
    'debit_card_stored_payment_details',
    'ach_stored_payment_details',
    'payment_requests',
    'payment_request_delivery_events',
    'payment_request_documents',
    'document_templates',
    'payment_transactions',
    'payment_transaction_lifecycle_events',
    'credentials',
    'permissions_bundles',
    'permissions_policy_grants',
    'legal_documents',
    'user_legal_document_acceptances',
    'processors',
    'merchant_accounts',
    'logical_terminals',
    'terminal_devices',
]);

export const TransactionLogOperationSchema: z.ZodType<TransactionLogOperation> = z.enum([
    'INSERT',
    'UPDATE',
    'DELETE',
]);

export const TransactionLogPaginationCursorQuerySchema: z.ZodType<string | undefined> = z
    .preprocess(
        (value: unknown): unknown => {
            if (value === undefined || value === null) {
                return undefined;
            }
            const stringParseResult = z.string().safeParse(value);
            if (!stringParseResult.success) {
                return value;
            }
            const trimmed = stringParseResult.data.trim();
            return trimmed.length === 0 ? undefined : trimmed;
        },
        z.string().min(1).optional()
    )
    .optional();

export const TransactionLogSearchRequestSchema: z.ZodType<TransactionLogSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: AuthorizationPaginationLimitQuerySchema,
        cursor: TransactionLogPaginationCursorQuerySchema,
    })
    .strict();

export const TransactionLogListItemSchema = z.object({
    transaction_log_ref: NonEmptyStringSchema,
    timestamp: z.string().datetime(),
    table_name: TransactionLogTableNameSchema,
    operation: TransactionLogOperationSchema,
    table_row: JsonValueSchema,
    ip_address: NonEmptyStringSchema,
    user_agent: NonEmptyStringSchema,
}) satisfies z.ZodType<TransactionLogListItem>;

export const ListTransactionLogPageResponseSchema = z.object({
    transaction_log: z.array(TransactionLogListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
}) satisfies z.ZodType<ListTransactionLogPageResponse>;
